/**
 * K108-R Batch 4 — Learning History card model (pure).
 *
 * Combines a launched training session (training_consumption_sessions +
 * training_artifacts) with the Batch 2 monitoring summary and the Batch 3
 * attestation (session.metadata.trainingAttestation) into a display card.
 *
 * HONEST BY DESIGN: shows both "OptiBay observed…" and "You reported…"; a
 * mismatch is surfaced NEUTRALLY as "Needs review" — never accusatory, never
 * "verified completion".
 */
import type { TrainingSessionMonitoringSummary } from '@/lib/training/trainingSessionMonitoringSummary';
import { ticketDisplayNumber } from '@/lib/tickets/ticketDisplayNumber';
import { formatAwayDuration } from '@/lib/training/mobileTrainingEstimate';

export type HistoryStatus =
  | 'In progress'
  | 'Partially completed'
  | 'Completed'
  | 'User-reported complete'
  | 'Needs review'
  | 'Reviewed – report accepted'
  | 'Reviewed – partial'
  | 'Follow-up requested'
  | 'Reviewed – no action'
  | 'Skipped / not relevant'
  | 'Superseded'
  | 'Evidence unavailable';

/** K108-X: an owner/admin review recorded on the session (metadata.trainingReview). */
export function trainingReview(session: HistorySessionInput): Record<string, unknown> | null {
  const raw = (session.metadata ?? {})['trainingReview'];
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
}

/** Reviewed status derived from the admin review action. Neutral, never accusatory. */
export function reviewedStatus(action: unknown): HistoryStatus {
  switch (action) {
    case 'accept_report':
      return 'Reviewed – report accepted';
    case 'mark_partial':
      return 'Reviewed – partial';
    case 'request_retake':
      return 'Follow-up requested';
    default:
      return 'Reviewed – no action';
  }
}

export interface HistorySessionInput {
  id: string;
  ticket_id: string | null;
  completion_status: string | null;
  opened_at: string | null;
  updated_at?: string | null;
  confirmed_at?: string | null;
  // K108-Y resume inputs (from the session row).
  opened_url_snapshot?: string | null;
  expected_duration_minutes?: number | null;
  metadata: Record<string, unknown> | null;
  artifact: {
    id: string;
    title: string | null;
    provider_name?: string | null;
    artifact_type?: string | null;
    /** Core-100 R4 CORE-O: false when the training has been retired (it is never deleted once used). */
    is_active?: boolean | null;
  } | null;
  /** Embedded ticket (for the readable number). The raw ticket_id stays for nav. */
  ticket?: {
    id: string;
    simple_ticket_number?: number | null;
    ticket_number?: string | null;
  } | null;
}

export interface TrainingHistoryCard {
  sessionId: string;
  /** Artifact this session is for (for a quiz / relaunch); null if unknown. */
  artifactId: string | null;
  title: string;
  provider: string | null;
  /** Raw ticket UUID — INTERNAL only (lookup/navigation), never displayed. */
  ticketId: string | null;
  /** Readable ticket number (e.g. "267") for display, or null if none. */
  ticketDisplay: string | null;
  lastActivityAt: string | null;
  /** Observed engagement % (active/duration). NOT the max reached position. */
  monitoredPercent: number | null;
  /** Max reached position in the video (%) — separate metric, not the headline. */
  maxReachedPositionPercent: number | null;
  /** Actual provider video duration (seconds), when known. */
  videoDurationSec: number | null;
  /** Observed engagement in minutes — the no-duration fallback. */
  observedMinutes: number | null;
  evidenceUnavailable: boolean;
  confidence: 'low' | 'medium' | null;
  userReported: string | null;
  mismatch: boolean;
  status: HistoryStatus;
  /** "OptiBay observed: 73% engagement" | "OptiBay observed: No monitored evidence available" */
  observedLine: string;
  /** "You reported: Partially completed" | null when no attestation. */
  reportedLine: string | null;
  /** Neutral note when observed and reported differ; null otherwise. */
  mismatchNote: string | null;
  /** Owner/admin review outcome + note, when reviewed; null otherwise. */
  reviewedLine: string | null;
  /** True when this session came from the unverified mobile launch flow. */
  mobile: boolean;
  /** "Opened on mobile — not complete…" note for a non-completed mobile record. */
  mobileNote: string | null;
  /**
   * Additive quiz KNOWLEDGE signal from metadata.trainingQuiz (Phase Q1). NEVER
   * changes the status/confidence — a passed quiz is understanding, not watch or
   * Companion/provider verification. null when no attempt recorded.
   */
  quiz: { status: 'passed' | 'not_passed'; scorePct: number | null } | null;
  /** "Quiz passed" / "Quiz not passed" — additive badge; null when no attempt. */
  quizLabel: string | null;
  /** K108-Y: the session is still pending → the owner may resume it. */
  resumable: boolean;
  /**
   * Core-100 R4 CORE-O: the training was retired (or is no longer available). History keeps the record, but retired
   * content is not presented as current — no resume, no re-open.
   */
  retired: boolean;
  /**
   * K108-Y resume launch payload for the SAME session (null when not resumable or
   * the artifact URL is missing → the UI shows a disabled/neutral state).
   */
  resume: {
    sessionId: string;
    artifactId: string;
    artifactType: string;
    targetUrl: string;
    expectedDurationMinutes: number | null;
    ticketId: string | null;
  } | null;
  /**
   * A COMPLETED / user-reported-complete row can still be re-opened (re-watch).
   * Re-opening starts a FRESH launch (new session) and does NOT change the prior
   * completion status. Null when there is no completion or no artifact URL.
   */
  reopen: {
    artifactId: string;
    artifactType: string;
    targetUrl: string;
    expectedDurationMinutes: number | null;
    ticketId: string | null;
  } | null;
}

const REVIEW_ACTION_LABELS: Record<string, string> = {
  accept_report: 'Owner/admin accepted the user report',
  mark_partial: 'Owner/admin marked this partially completed',
  request_retake: 'Owner/admin requested a retake / follow-up',
  dismiss: 'Owner/admin reviewed — no action needed',
};

/** Neutral reviewed line for History, with the admin note when present. */
export function historyReviewedLine(session: HistorySessionInput): string | null {
  const review = trainingReview(session);
  if (!review) return null;
  const label = REVIEW_ACTION_LABELS[String(review.action)] ?? 'Owner/admin reviewed';
  const note = typeof review.note === 'string' && review.note.trim() ? ` — “${review.note.trim()}”` : '';
  return `${label}${note}`;
}

const USER_REPORTED_LABELS: Record<string, string> = {
  completed: 'Completed in full',
  partial: 'Partially completed',
  abandoned: 'Did not get to it yet',
  skipped: 'Not relevant / skipped',
  monitoring_incorrect: "OptiBay's monitored progress looks wrong",
};

function attestation(session: HistorySessionInput): Record<string, unknown> | null {
  const raw = (session.metadata ?? {})['trainingAttestation'];
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
}

/** The mobile-launch estimate written by useMobileTrainingOutcome, if present. */
function mobileEstimate(session: HistorySessionInput): Record<string, unknown> | null {
  const raw = (session.metadata ?? {})['mobileEstimate'];
  if (!raw || typeof raw !== 'object') return null;
  const est = raw as Record<string, unknown>;
  return est.source === 'mobile_unverified_return' ? est : null;
}

/**
 * Honest, low-confidence note for a mobile record that was opened but not
 * completed ("Do it later"). Never claims verified watch time.
 */
/** Reads the additive quiz signal from metadata.trainingQuiz (Phase Q1). */
export function trainingQuizSignal(
  session: HistorySessionInput,
): { status: 'passed' | 'not_passed'; scorePct: number | null } | null {
  const raw = (session.metadata ?? {})['trainingQuiz'];
  if (!raw || typeof raw !== 'object') return null;
  const q = raw as Record<string, unknown>;
  if (typeof q.quiz_passed !== 'boolean') return null;
  const scorePct = typeof q.quiz_score_pct === 'number' ? q.quiz_score_pct : null;
  return { status: q.quiz_passed ? 'passed' : 'not_passed', scorePct };
}

export function historyMobileNote(session: HistorySessionInput): string | null {
  const est = mobileEstimate(session);
  if (!est) return null;
  // Only for NOT-complete mobile records — a completed mobile record shows the
  // normal "User-reported complete" status instead.
  if (session.completion_status === 'completed') return null;
  const elapsed =
    typeof est.elapsed_seconds === 'number' ? est.elapsed_seconds : 0;
  const pct =
    typeof est.estimated_completion_pct === 'number'
      ? est.estimated_completion_pct
      : null;
  const away = `You were away for about ${formatAwayDuration(elapsed)}.`;
  const est_line =
    pct !== null
      ? `Estimated possible completion: ${pct}%.`
      : 'Completion could not be estimated.';
  return `Opened on mobile — not complete. ${away} ${est_line} Not Companion verified.`;
}

export function historyObservedLine(
  monitoredPercent: number | null,
  evidenceUnavailable: boolean,
  observedMinutes?: number | null,
): string {
  if (evidenceUnavailable) return 'OptiBay observed: No monitored evidence available';
  if (monitoredPercent != null) return `OptiBay observed: ${Math.round(monitoredPercent)}% engagement`;
  if (observedMinutes != null && observedMinutes > 0) {
    return `OptiBay observed: ~${observedMinutes} min engagement`;
  }
  return 'OptiBay observed: No monitored evidence available';
}

export function userReportedLabel(userReported: string | null | undefined): string | null {
  if (!userReported) return null;
  return USER_REPORTED_LABELS[userReported] ?? userReported;
}

export function deriveHistoryStatus(
  session: HistorySessionInput,
  summary: TrainingSessionMonitoringSummary | null,
): HistoryStatus {
  // K108-X: an owner/admin review resolves the mismatch — its outcome takes
  // precedence over "Needs review".
  const review = trainingReview(session);
  if (review) return reviewedStatus(review.action);

  const att = attestation(session);
  if (att && att.mismatch === 'user_attestation_mismatch') return 'Needs review';

  const status = session.completion_status;
  const evidenceUnavailable = summary?.evidenceUnavailable ?? true;

  if (status === 'completed') return evidenceUnavailable ? 'User-reported complete' : 'Completed';
  if (status === 'partial') return 'Partially completed';
  if (status === 'skipped' || status === 'abandoned') return 'Skipped / not relevant';
  // K108-Z2f: a newer launch replaced this attempt. Defensive — the history query
  // already filters superseded out — but if one reaches here it is labelled
  // 'Superseded' and is NOT resumable (not in the resumable set below).
  if (status === 'superseded') return 'Superseded';
  // pending / null → launched, not yet confirmed.
  return evidenceUnavailable ? 'Evidence unavailable' : 'In progress';
}

/** Neutral note when the user's report differs from monitored engagement. */
export function historyMismatchNote(session: HistorySessionInput): string | null {
  const att = attestation(session);
  if (att && att.mismatch === 'user_attestation_mismatch') {
    return 'Report differs from monitored engagement. Monitoring may be incomplete, or the training may have been completed elsewhere.';
  }
  return null;
}

export function buildTrainingHistoryCard(
  session: HistorySessionInput,
  summary: TrainingSessionMonitoringSummary | null,
): TrainingHistoryCard {
  const att = attestation(session);
  const quizSignal = trainingQuizSignal(session);
  const monitoredPercent = summary?.monitoredPercent ?? null;
  const observedMinutes = summary?.observedMinutes ?? null;
  const evidenceUnavailable = summary?.evidenceUnavailable ?? true;
  const userReported = att && typeof att.userReported === 'string' ? (att.userReported as string) : null;
  const reportedLbl = userReportedLabel(userReported);

  const lastActivityAt =
    summary?.lastActivityAt ?? session.confirmed_at ?? session.updated_at ?? session.opened_at ?? null;

  // K108-Y: a session is resumable while it is still IN FLIGHT — In progress,
  // Evidence unavailable (both pending), OR Partially completed (the tech can
  // continue and later update their report). NOT completed / reviewed / follow-up /
  // skipped. `resume` carries the launch payload only when the artifact URL exists.
  const status = deriveHistoryStatus(session, summary);
  // Core-100 R4 CORE-O: retired (is_active false) or unavailable (no artifact readable) training is not re-launched.
  const retired = !session.artifact || session.artifact.is_active === false;
  const resumable =
    !retired && (
    status === 'In progress' ||
    status === 'Evidence unavailable' ||
    status === 'Partially completed');
  const targetUrl = session.opened_url_snapshot ?? null;
  const resume =
    resumable && targetUrl
      ? {
          sessionId: session.id,
          artifactId: session.artifact?.id ?? '',
          artifactType: session.artifact?.artifact_type ?? 'long_video',
          targetUrl,
          expectedDurationMinutes: session.expected_duration_minutes ?? null,
          ticketId: session.ticket_id ?? null,
        }
      : null;
  // Completed artifacts remain re-openable (re-watch) via a FRESH launch — the
  // prior completion status is untouched. Requires the artifact URL.
  const reopenable = !retired && (status === 'Completed' || status === 'User-reported complete');
  const reopen =
    reopenable && targetUrl
      ? {
          artifactId: session.artifact?.id ?? '',
          artifactType: session.artifact?.artifact_type ?? 'long_video',
          targetUrl,
          expectedDurationMinutes: session.expected_duration_minutes ?? null,
          ticketId: session.ticket_id ?? null,
        }
      : null;

  return {
    sessionId: session.id,
    artifactId: session.artifact?.id ?? null,
    title: session.artifact?.title ?? 'Training',
    provider: session.artifact?.provider_name ?? null,
    ticketId: session.ticket_id ?? null,
    ticketDisplay: ticketDisplayNumber(session.ticket),
    lastActivityAt,
    monitoredPercent,
    maxReachedPositionPercent: summary?.maxVideoProgressPercent ?? null,
    videoDurationSec: summary?.videoDurationSec ?? null,
    observedMinutes,
    evidenceUnavailable,
    confidence: summary?.confidence ?? null,
    userReported,
    mismatch: !!(att && att.mismatch === 'user_attestation_mismatch'),
    status,
    mobile: mobileEstimate(session) !== null,
    mobileNote: historyMobileNote(session),
    quiz: quizSignal,
    quizLabel: quizSignal
      ? quizSignal.status === 'passed'
        ? 'Quiz passed'
        : 'Quiz not passed'
      : null,
    resumable,
    retired,
    resume,
    reopen,
    reviewedLine: historyReviewedLine(session),
    observedLine: historyObservedLine(monitoredPercent, evidenceUnavailable, observedMinutes),
    reportedLine: reportedLbl ? `You reported: ${reportedLbl}` : null,
    mismatchNote: historyMismatchNote(session),
  };
}

/** Most-recent-first by lastActivityAt (ISO); cards without a timestamp sink last. */
export function sortHistoryCards(cards: TrainingHistoryCard[]): TrainingHistoryCard[] {
  return [...cards].sort((a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''));
}
